'use client'

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

// MUI Imports
import Card from '@mui/material/Card'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'

// Third-party Imports
import { Icon } from '@iconify/react'

// Component Imports
import CustomAvatar from '../../../../../components/common/Template_Layouts/@core/components/mui/Avatar'

const PremiumModuleCards = ({ stats, onStatusClick, moduleName = 'Task', customCards, activeFilters = ['All'], px = '24px' }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);

    const data = customCards || [
        {
            title: stats?.total || 0,
            subtitle: `Total ${moduleName}s`,
            icon: 'ri:list-check',
            color: 'primary',
            key: 'All'
        },
        {
            title: stats?.completed || 0,
            subtitle: 'Completed',
            icon: 'ri:checkbox-circle-line',
            color: 'success',
            key: 'Completed'
        },
        {
            title: stats?.overdue || 0,
            subtitle: 'Overdue',
            icon: 'ri:error-warning-line',
            color: 'error',
            key: 'Overdue'
        },
        {
            title: stats?.open || 0,
            subtitle: 'Open',
            icon: 'ri:time-line',
            color: 'info',
            key: 'Open'
        }
    ]

    // Helper to get color based on MUI skin
    const getActiveStyles = (color, isActive, itemKey) => {
        if (!isActive || itemKey === 'All') return { backgroundColor: 'transparent', border: '1.5px solid transparent' };

        switch (color) {
            case 'primary': return { backgroundColor: '#eff6ff', border: '1.5px solid #3b82f6' };
            case 'success': return { backgroundColor: '#f0fdf4', border: '1.5px solid #22c55e' };
            case 'error': return { backgroundColor: '#fff1f2', border: '1.5px solid #ef4444' };
            case 'warning': return { backgroundColor: '#fffbeb', border: '1.5px solid #f59e0b' };
            case 'info': return { backgroundColor: '#ecfeff', border: '1.5px solid #06b6d4' };
            case 'secondary': return { backgroundColor: '#f8fafc', border: '1.5px solid #64748b' };
            default: return { backgroundColor: '#f8fafc', border: '1.5px solid #e2e8f0' };
        }
    };

    return (
        <Card sx={{
            boxShadow: 'none',
            border: 'none',
            borderRadius: 0,
            width: '100%',
            margin: 0,
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
            position: 'sticky',
            top: '64px',
            zIndex: 99,
            overflow: 'visible !important'
        }}>
            <AnimatePresence initial={false}>
                {!isCollapsed && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        style={{ overflow: 'hidden' }}
                    >
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: `16px ${px}`,
                            width: '100%',
                            flexWrap: 'nowrap',
                            gap: '20px'
                        }}>
                            {data.map((item, index) => {
                                const isActive = activeFilters.includes(item.key);
                                const activeStyles = getActiveStyles(item.color, isActive, item.key);

                                return (
                                    <Tooltip key={index} title={`${isActive ? 'Remove' : 'Add'} filter: ${item.subtitle}`} arrow placement="top">
                                        <div
                                            onClick={() => onStatusClick && onStatusClick(item.key)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                padding: '10px 16px',
                                                borderRadius: '12px',
                                                flex: 1,
                                                minWidth: '180px',
                                                maxWidth: '320px',
                                                ...activeStyles,
                                                boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.05)' : 'none'
                                            }}
                                            className={`premium-card-item ${isActive ? 'active' : ''}`}
                                        >
                                            <div className="flex items-center gap-3 w-full">
                                                <CustomAvatar variant='rounded' size={44} skin={isActive ? 'filled' : 'light'} color={item.color}>
                                                    <Icon icon={item.icon} fontSize={26} />
                                                </CustomAvatar>
                                                <div className='flex flex-col'>
                                                    <Typography variant='h6' sx={{ fontWeight: 700, color: isActive ? '#1a202c' : '#2d3748', lineHeight: 1.1, fontSize: '1.2rem' }}>
                                                        {item.title}
                                                    </Typography>
                                                    <Typography sx={{ color: isActive ? '#4a5568' : '#718096', fontSize: '12px', fontWeight: 600, marginTop: '2px' }}>
                                                        {item.subtitle}
                                                    </Typography>
                                                </div>
                                            </div>
                                        </div>
                                    </Tooltip>
                                );
                            })}

                            {/* Show All / Reset Button */}
                            <Tooltip title="Show all records (Clear filters)" arrow placement="top">
                                <div
                                    onClick={() => onStatusClick && onStatusClick('ResetAll')}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '10px 18px',
                                        borderRadius: '12px',
                                        border: '1.5px solid #e2e8f0',
                                        backgroundColor: '#f8fafc',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        minWidth: 'fit-content',
                                        flex: '0 0 auto',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#f1f5f9';
                                        e.currentTarget.style.borderColor = '#cbd5e1';
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#f8fafc';
                                        e.currentTarget.style.borderColor = '#e2e8f0';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    <div style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '8px',
                                        backgroundColor: '#ffffff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.03)'
                                    }}>
                                        <Icon icon="ri:filter-off-line" fontSize={20} color="#64748b" />
                                    </div>
                                    <Typography sx={{ color: '#475569', fontSize: '13px', fontWeight: 800, whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Show All
                                    </Typography>
                                </div>
                            </Tooltip>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Centered Toggle Button */}
            <div
                style={{
                    position: 'absolute',
                    bottom: '-22px', // Perfectly centered on the border line
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 100, // Significantly higher to ensure it's above the border and table but lower than menus
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Tooltip title={isCollapsed ? "Show Statistics" : "Hide Statistics"} arrow placement="bottom">
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        style={{
                            backgroundColor: '#3b82f6',
                            color: '#ffffff',
                            width: '42px',
                            height: '42px',
                            borderRadius: '50%',
                            border: '3px solid #ffffff',
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            outline: 'none',
                            padding: 0,
                            zIndex: 100
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.1)';
                            e.currentTarget.style.backgroundColor = '#2563eb';
                            e.currentTarget.style.boxShadow = '0 6px 18px rgba(59, 130, 246, 0.5)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.backgroundColor = '#3b82f6';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                        }}
                    >
                        <Icon
                            icon={isCollapsed ? 'ri:arrow-down-s-line' : 'ri:arrow-up-s-line'}
                            fontSize={26}
                            style={{ transition: 'transform 0.3s ease' }}
                        />
                    </button>
                </Tooltip>
            </div>
        </Card>
    )
}

export default PremiumModuleCards
